import {useMemo} from 'react'
import {useSelector} from 'react-redux'
import {getOfferVariant} from 'components/Offer/Shared/utils'
import {OldHotelOfferEntity} from 'modules/sapiSearch/offersMapping'
import {
  getIsPrimaryOfferGreatDeal,
  getOfferAccessFlowSettings
} from 'modules/sapiSearch/selectors'
import {
  getAnchorPrice,
  getAnchorPriceTotal,
  getTopOffers
} from 'modules/sapiSearch/utils'
import {getCheapestTopOfferIndex} from 'utils/offers'

import {getHasFreeCancellation} from '@vio/core/src/offer/business/cancellationPenalties'
import getIsBofhOffer from '@vio/core/src/offer/business/isBofhOffer'

const OFFER_TO_SHOW = 0

interface Props {
  offerEntity: OldHotelOfferEntity
  hotelId: string
  offerId?: string
  withGreatDealTag?: boolean
}

export const useOfferContainer = ({
  offerEntity,
  offerId,
  hotelId,
  withGreatDealTag
}: Props) => {
  const allOffers = offerEntity?.offers ?? []
  const topOffers = getTopOffers(offerEntity)
  const offerIndex: number = useMemo(() => {
    if (offerId) return topOffers.findIndex(offer => offer.id === offerId)
    return OFFER_TO_SHOW
  }, [offerId, topOffers])

  const primaryOffer = topOffers[offerIndex]
  const isCheapest = getCheapestTopOfferIndex(topOffers) === offerIndex
  const isBofhOffer = getIsBofhOffer(primaryOffer)

  const isGreatDealOffer = useSelector(state =>
    getIsPrimaryOfferGreatDeal(state, hotelId)
  )

  const offerVariant = useMemo(() => {
    return getOfferVariant({
      isCheapest,
      isBofhOffer,
      isGreatDealOffer: isGreatDealOffer && withGreatDealTag
    })
  }, [isCheapest, isBofhOffer, isGreatDealOffer, withGreatDealTag])

  const anchorPrice = getAnchorPrice(offerEntity)
  const anchorPriceTotal = getAnchorPriceTotal(offerEntity)
  const hasFreeCancellation = getHasFreeCancellation(
    primaryOffer?.cancellationPenalties
  )

  const {isAppRequired} = useSelector(state =>
    getOfferAccessFlowSettings(state, hotelId, primaryOffer?.id)
  )

  return {
    allOffers,
    topOffers,
    primaryOffer,
    primaryOfferIndex: offerIndex,
    offerVariant,
    anchorPrice,
    anchorPriceTotal,
    hasFreeCancellation,
    // @search This property is unused consider removing it.
    isAppLockedDeal: isAppRequired,
    hasOffers: allOffers.length > 0,
    isPrimaryOfferCheapest: isCheapest,
    isPrimaryOfferGreatDeal: isGreatDealOffer
  }
}
